import React, { useState } from 'react';
import { BulkActionButton } from '../BulkActionButton';
import {
  checkIfSelectedCandidatesAreInTheSameInterviewStage,
  checkIfSelectedCandidatesHaveSameJob,
} from '../utils';
import { redirectToBulkSelfSchedulingPage } from '../../../../../../../utils/applicant_tracking/buildSelfScheduling';
import { NewListCandidate } from '../../../../../../../entities/v1/applicant_tracking/NewListCandidate';
import { BulkSelfSchedulingType } from '../../../../../../../services/dtos/BulkSelfSchedulingDto';
import { UserService } from '../../../../../../../services/v1/UserService';
import { ConnectionStatus } from '../../../../../../../entities/UserIntegration';
import { useFeatureFlags } from '../../../../../../../contexts/FeatureFlagContext';
import { LoadingSpinnerIcon } from '../../../../../../../components/LoadingSpinnerIcon';
import { DisconnectedSchedulingIntegrationModal } from './DisconnectedSchedulingIntegrationModal';
import { Alert, AlertType } from '../../../../../../../components/Alert';

interface PropTypes {
  selectedCandidates: NewListCandidate[];
  bulkSelectedIds: number[] | null;
}

async function ensureIntegrationConnected(
  featureFlagEnabled: boolean,
): Promise<boolean> {
  if (!featureFlagEnabled) return true;

  const updatedIntegration = await UserService.checkSchedulingUserIntegration();

  return updatedIntegration.connectionStatus === ConnectionStatus.Connected;
}

function navigateToSelfScheduling(selectedCandidates: NewListCandidate[]) {
  const [first] = selectedCandidates;
  const jobId = first.application.job.id;
  const interviewStageId = first.application.currentStage.interviewStageId;
  const applicationIds = selectedCandidates.map((c) => c.application.id);

  redirectToBulkSelfSchedulingPage(
    BulkSelfSchedulingType.Applications,
    jobId,
    interviewStageId,
    selectedCandidates.length,
    applicationIds,
  );
}

function handleError(error: unknown, featureFlagEnabled: boolean) {
  if (featureFlagEnabled) {
    console.error(
      'Failed to update disconnected scheduling integration:',
      error,
    );
  }
}

export function SelfSchedulingButton(props: PropTypes) {
  const [isSchedulingLoading, setIsSchedulingLoading] =
    useState<boolean>(false);
  const [isSchedulingModalOpen, setIsSchedulingModalOpen] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState<boolean>(false);
  const isBulkMode = props.bulkSelectedIds !== null;
  const actionDisabled =
    isBulkMode ||
    props.selectedCandidates.length === 0 ||
    !checkIfSelectedCandidatesHaveSameJob(props.selectedCandidates) ||
    !checkIfSelectedCandidatesAreInTheSameInterviewStage(
      props.selectedCandidates,
    );
  const toolTipText = actionDisabled
    ? `Bulk Self-Scheduling is only available for ${
        isBulkMode ? 'individually selected ' : ''
      }candidates that share the same job and stage. The stage needs to include an interview.`
    : 'Send Self-Scheduling Link';
  const { atsUpdateSchedulingUserIntegrationUserInfoEnabled } =
    useFeatureFlags();

  const onClick = async () => {
    if (props.selectedCandidates.length === 0) return;
    if (!atsUpdateSchedulingUserIntegrationUserInfoEnabled) {
      navigateToSelfScheduling(props.selectedCandidates);
      return;
    }

    setIsSchedulingLoading(true);
    try {
      const integrationConnected = await ensureIntegrationConnected(
        atsUpdateSchedulingUserIntegrationUserInfoEnabled,
      );
      if (!integrationConnected) {
        setIsSchedulingModalOpen(true);
        return;
      }

      navigateToSelfScheduling(props.selectedCandidates);
    } catch (error) {
      setIsAlertOpen(true);
      handleError(error, atsUpdateSchedulingUserIntegrationUserInfoEnabled);
    } finally {
      setIsSchedulingLoading(false);
    }
  };

  return (
    <>
      {isAlertOpen && (
        <Alert
          type={AlertType.Danger}
          clearable={true}
          autoClearTimeout={4000}
          onClose={() => {
            setIsAlertOpen(false);
          }}
        >
          {
            "We couldn't confirm the status of your calendar connection. Please try again later."
          }
        </Alert>
      )}
      <DisconnectedSchedulingIntegrationModal
        isSchedulingLoading={isSchedulingLoading}
        setIsSchedulingModalOpen={setIsSchedulingModalOpen}
        isSchedulingModalOpen={isSchedulingModalOpen}
      />
      {isSchedulingLoading ? (
        <LoadingSpinnerIcon className='me-2' />
      ) : (
        <BulkActionButton
          iconName='bi-calendar2-check'
          toolTipText={toolTipText}
          disabled={actionDisabled}
          onClick={onClick}
          analyticsId='bulk-self-scheduling-button'
        />
      )}
    </>
  );
}
